import { useState } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

import { UserHosting200RowsItem } from '@newfold/huapi-js/src/index.schemas';

import { useBrandInfo } from '~/hooks/useBrandInfo';
import HowToCarousel from '~/Nova/scenes/Site/components/HowToCarousel';

import AiCreditBalanceCard from './components/AiCreditBalanceCard';
import AiToolingCardsSkeleton from './components/AiToolingCardsSkeleton';
import AiToolingErrorState from './components/AiToolingErrorState';
import ApiKeysCard from './components/ApiKeysCard';
import ManageApiKeysModal from './components/ManageApiKeysModal';
import useAiToolingData from './hooks/useAiToolingData';
import { getInstalledAppHowToItem, resolveAiBrand } from './utils/helpers';

interface AiToolingProps {
  hostingPlanDetails?: UserHosting200RowsItem | null;
}

const AiTooling = ({ hostingPlanDetails = null }: AiToolingProps) => {
  const {
    isLoading,
    isError,
    isRefreshing,
    refetch,
    creditBalance,
    creditExpiryDate,
    hasLlmAddon,
    addonId,
  } = useAiToolingData();
  const [isManageKeysOpen, setIsManageKeysOpen] = useState(false);

  const brandInfo = useBrandInfo();
  const aiBrand = resolveAiBrand(brandInfo.brandFromJWT);
  const {
    bluehostAPIKeysArticleUrl,
    purchaseAiCreditArticleUrl,
    understandAiCreditArticleUrl,
  } = brandInfo;

  const { account_info: { image = {} } = {} } = hostingPlanDetails || {};

  const installedApp = ((image?.app || image?.software) ?? '')
    .trim()
    .toLowerCase();

  const handleManageKeys = () => setIsManageKeysOpen(true);
  const handleCloseManageKeys = () => setIsManageKeysOpen(false);
  const { appHowToItems, creditPricingItems } = getInstalledAppHowToItem({
    installedApp,
    aiBrand,
    brandInfo,
  });

  // The data cards swap between loading / error / loaded. The how-to card only
  // renders when a learn-more article URL is configured.
  const renderDataCards = () => {
    if (isLoading) {
      return <AiToolingCardsSkeleton />;
    }

    if (isError) {
      return <AiToolingErrorState onRetry={refetch} />;
    }

    return (
      <>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          data-testid="ai-tooling-cards"
        >
          <Box flex={1}>
            <AiCreditBalanceCard
              credits={creditBalance}
              creditExpiryDate={creditExpiryDate}
              hasLlmAddon={hasLlmAddon}
              onRefresh={refetch}
              isRefreshing={isRefreshing}
            />
          </Box>
          <Box flex={1}>
            <ApiKeysCard
              hasLlmAddon={hasLlmAddon}
              onManageKeys={handleManageKeys}
            />
          </Box>
        </Stack>

        <ManageApiKeysModal
          open={isManageKeysOpen}
          onClose={handleCloseManageKeys}
          addonId={addonId}
        />
      </>
    );
  };
  return (
    <Stack spacing={4} data-testid="ai-tooling-section">
      {renderDataCards()}
      <HowToCarousel
        items={[
          ...appHowToItems,
          {
            type: 'howto',
            title: `aiTool.${aiBrand}.titleAPIKeys`,
            articleUrl: bluehostAPIKeysArticleUrl,
            description: 'aiTool.descAPIKeys',
            displayVideoArticleCard: false,
          },
          {
            type: 'howto',
            title: `aiTool.${aiBrand}.titlePurchaseCredit`,
            articleUrl: purchaseAiCreditArticleUrl,
            description: 'aiTool.descPurchaseCredit',
            displayVideoArticleCard: false,
          },
          {
            type: 'howto',
            title: `aiTool.${aiBrand}.titleUnderstandAICredit`,
            description: 'aiTool.descUnderstandAICredit',
            articleUrl: understandAiCreditArticleUrl,
            displayVideoArticleCard: false,
          },
          ...creditPricingItems,
        ]}
      />
    </Stack>
  );
};

export default AiTooling;
